import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowRight } from 'lucide-react';
import NavShell from '../components/NavShell';
import { fetchPointsLedger, fetchActivityHistory, type ActivityHistoryRow } from '../lib/api';
import {
  ledgerLabel,
  ledgerCategory,
  activityOutcomeLabel,
  LEDGER_CATEGORY_LABELS,
  LEDGER_CATEGORY_ORDER,
  type LedgerCategory,
  type PointsLedger,
} from '@swipeopinion/shared';
import { useSupportChat } from '../hooks/useSupportChat';

// ── Types ─────────────────────────────────────────────────────────────────
type FilterValue = 'all' | LedgerCategory;
// One normalized row list, whether in ledger-only mode (all rows 'earning') or
// merged mode (earning + activity). ActivityHistoryRow already carries the
// discriminator; a ledger entry maps onto the 'earning' shape.
type DisplayRow = ActivityHistoryRow;

const EXCHANGE_LABEL: Record<string, string> = {
  purespectrum: 'PureSpectrum',
  research_for_good: 'Research for Good',
  rfg: 'Research for Good',
  manual: 'Swipe Opinion',
  cint: 'Cint',
};
function exchangeLabel(name: string): string {
  return EXCHANGE_LABEL[name] ?? 'Survey';
}

function ledgerToRow(e: PointsLedger): DisplayRow {
  return {
    kind: 'earning',
    id: e.id,
    created_at: (e.created_at instanceof Date ? e.created_at.toISOString() : e.created_at) as string,
    source_type: e.source_type,
    source_id: e.source_id,
    delta: e.delta,
    note: e.note,
    is_pending: e.is_pending,
    pending_until:
      e.pending_until instanceof Date ? e.pending_until.toISOString() : e.pending_until,
  };
}

// ── Pure helpers (exported for tests) ───────────────────────────────────────

/** Signed-delta display: credit (+, green) vs debit (−, red). */
export function deltaDisplay(delta: number): { text: string; className: string } {
  if (delta < 0) return { text: `−${Math.abs(delta)} pts`, className: 'text-[#EF4444]' };
  return { text: `+${delta} pts`, className: 'text-[#10B981]' };
}

/** Pending affordance from is_pending / pending_until. Null once settled. */
export function pendingLabel(entry: { is_pending: boolean; pending_until: string | Date | null }): string | null {
  if (!entry.is_pending) return null;
  if (!entry.pending_until) return 'Pending';
  const d = new Date(entry.pending_until);
  return `Pending · releases ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

/** Contact-support belongs on disputable SURVEY earning rows only — a completed
 *  survey whose source_id is the exchange_session id the support flow keys on. */
export function canDisputeSource(sourceType: string): boolean {
  return sourceType === 'survey_completion';
}

function formatDate(iso: string | Date): string {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── UI ──────────────────────────────────────────────────────────────────────

export default function EarningHistoryPage() {
  return (
    <NavShell>
      <EarningHistoryContent />
    </NavShell>
  );
}

function EarningHistoryContent() {
  const [rows, setRows] = useState<DisplayRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState<FilterValue>('all');
  const [includeActivity, setIncludeActivity] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supportChat = useSupportChat();

  // Fetch a page in the current mode. Ledger-only (toggle OFF) hits the byte-stable
  // /me/ledger with the category filter; merged (toggle ON) hits /me/activity
  // (unfiltered — see the category-filter note below).
  async function loadPage(p: number): Promise<{ rows: DisplayRow[]; total: number }> {
    if (includeActivity) {
      const r = await fetchActivityHistory(p);
      return { rows: r.rows, total: r.total };
    }
    const r = await fetchPointsLedger(p, filter);
    if (r.categoryCounts) setCategoryCounts(r.categoryCounts);
    return { rows: r.entries.map(ledgerToRow), total: r.total };
  }

  // (Re)load page 1 whenever the filter or the activity toggle changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadPage(1)
      .then((r) => {
        if (cancelled) return;
        setRows(r.rows);
        setPage(1);
        setTotal(r.total);
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, includeActivity]);

  const hasMore = rows.length < total;

  async function loadMore() {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const r = await loadPage(next);
      setRows((prev) => [...prev, ...r.rows]);
      setPage(next);
      setTotal(r.total);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }

  const availableCategories = LEDGER_CATEGORY_ORDER.filter((c) => (categoryCounts[c] ?? 0) > 0);

  return (
    <motion.main
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="px-4 sm:px-6 py-6 space-y-6"
    >
      <header>
        <h1 className="text-2xl sm:text-3xl font-bold font-display text-[#111827] flex items-center gap-2">
          <Clock className="w-6 h-6 text-primary" />
          Earnings History
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Every point you've earned or spent — surveys, games, bonuses, and redemptions.
        </p>
      </header>

      {/* Category filter — ledger-only mode. Hidden when the activity toggle is on:
          the merged view is intentionally unfiltered (it's the "show me everything,
          including misses" mode), and activity rows have no source_type/category. */}
      {!includeActivity && availableCategories.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
          <FilterChip label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
          {availableCategories.map((c) => (
            <FilterChip
              key={c}
              label={LEDGER_CATEGORY_LABELS[c]}
              active={filter === c}
              onClick={() => setFilter(c)}
            />
          ))}
        </div>
      )}

      {/* Non-earning activity toggle — default OFF. */}
      <label className="flex items-center gap-2 text-sm text-[#6B7280] cursor-pointer select-none">
        <input
          type="checkbox"
          checked={includeActivity}
          onChange={(e) => setIncludeActivity(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        Include non-earning activity (surveys you didn't qualify for)
      </label>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
        {loading && <HistorySkeleton />}

        {!loading && error && (
          <div className="py-8 text-center text-sm text-[#6B7280]">
            Couldn't load your history right now. Please try again in a moment.
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-base text-[#111827] font-medium">
              {filter === 'all' || includeActivity ? 'No activity yet.' : 'Nothing in this category yet.'}
            </p>
            <p className="text-sm text-[#6B7280] mt-1">
              {filter === 'all' || includeActivity ? 'Head to the dashboard to get started.' : 'Try a different filter.'}
            </p>
            {(filter === 'all' || includeActivity) && (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1 mt-4 text-sm text-primary hover:text-primary-dark font-medium"
              >
                Go to dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {rows.map((row) =>
              row.kind === 'earning' ? (
                <EarningRowView key={`e:${row.id}`} row={row} onDispute={(sid) => supportChat.open(sid)} />
              ) : (
                <ActivityRowView key={`a:${row.id}`} row={row} onDispute={(sid) => supportChat.open(sid)} />
              ),
            )}
          </ul>
        )}

        {!loading && !error && hasMore && (
          <div className="pt-4 mt-2 border-t border-gray-100 flex justify-center">
            <button
              type="button"
              onClick={() => { void loadMore(); }}
              disabled={loadingMore}
              className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-dark font-medium disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </motion.main>
  );
}

function EarningRowView({
  row,
  onDispute,
}: {
  row: Extract<DisplayRow, { kind: 'earning' }>;
  onDispute: (sessionId: string) => void;
}) {
  const category = ledgerCategory(row.source_type);
  const pending = pendingLabel(row);
  const amount = deltaDisplay(row.delta);
  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-[#EEF2FF] text-primary">
              {LEDGER_CATEGORY_LABELS[category]}
            </span>
            {pending && (
              <span className="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                {pending}
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-[#111827] mt-1.5">
            {row.note ?? ledgerLabel(row.source_type)}
          </p>
          <p className="text-xs text-[#6B7280] mt-0.5">{formatDate(row.created_at)}</p>
        </div>
        <div className="flex items-center gap-4 sm:flex-col sm:items-end sm:gap-1">
          <span className={`text-sm font-semibold tabular-nums ${amount.className}`}>{amount.text}</span>
          {canDisputeSource(row.source_type) && row.source_id && (
            <button
              type="button"
              onClick={() => onDispute(row.source_id as string)}
              className="text-xs text-[#6B7280] hover:text-primary underline-offset-2 hover:underline"
            >
              Contact support
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function ActivityRowView({
  row,
  onDispute,
}: {
  row: Extract<DisplayRow, { kind: 'activity' }>;
  onDispute: (sessionId: string) => void;
}) {
  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-[#EEF2FF] text-primary">
              {exchangeLabel(row.exchange_name)}
            </span>
            <span className="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-100">
              {activityOutcomeLabel(row.status)}
            </span>
          </div>
          <p className="text-sm font-medium text-[#111827] mt-1.5">{row.survey_name}</p>
          <p className="text-xs text-[#6B7280] mt-0.5">{formatDate(row.created_at)}</p>
        </div>
        <div className="flex items-center gap-4 sm:flex-col sm:items-end sm:gap-1">
          {/* No points on a non-crediting attempt. */}
          <span className="text-sm font-semibold tabular-nums text-[#6B7280]">—</span>
          <button
            type="button"
            onClick={() => onDispute(row.session_id)}
            className="text-xs text-[#6B7280] hover:text-primary underline-offset-2 hover:underline"
          >
            Contact support
          </button>
        </div>
      </div>
    </li>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
        active ? 'bg-primary text-white' : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

function HistorySkeleton() {
  return (
    <ul className="divide-y divide-gray-100" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="py-4 first:pt-0 last:pb-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 space-y-2">
              <div className="flex gap-2">
                <div className="h-4 w-20 bg-gray-100 rounded-full animate-pulse" />
                <div className="h-4 w-16 bg-gray-100 rounded-full animate-pulse" />
              </div>
              <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
              <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
            </div>
            <div className="h-4 w-14 bg-gray-100 rounded animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}
